<template>
  <label :class="style.item">
    <input
      type="radio"
      class="sr-only"
      :name="groupName"
      :value="position.id"
      :checked="isActive"
      @change="$emit('selected', position)"
    />
    <span
      :class="style.drag"
      :data-drag-handle="!disableDrag || undefined"
      aria-hidden="true"
    >
      <strong v-if="!position.archivedAt">{{ position.sortOrder }}</strong>
      <strong v-else data-i="archive"></strong>
      <Icon icon="drag_indicator" />
    </span>
    <span :class="style.content">
      <img
        v-if="position.thumbnails?.sm"
        :src="position.thumbnails.sm"
        :alt="position.name"
      />

      <ThumbPlaceholder v-else style="color: var(--neutral-400)" />

      <span style="min-width: 0">
        <h3 class="truncate">{{ position.name }}</h3>
        <span class="flex gap-1 flex-wrap">
          <Badge
            v-if="!position.isInScope"
            variant="alert"
            class="text-xs leading-none"
          >
            {{ $t('positions:actions.descoped') }}
          </Badge>
          <Badge
            v-if="position.archivedAt"
            variant="secondary"
            class="text-xs leading-none"
          >
            {{ $t('positions:actions.archived') }}
          </Badge>
          <Badge
            v-for="tag in productTypeTags"
            :key="tag"
            variant="success"
            class="text-xs leading-none"
          >
            {{ tag }}
          </Badge>
        </span>
        <div v-if="productionStatus" class="mt-2">
          <RevisionReviewBadge
            :status="productionStatus"
            class="text-xs leading-none clean"
          />
        </div>
      </span>
      <span class="font-medium whitespace-nowrap">
        {{ $t('order:filesCard.qty') }}: {{ position.quantity }}
      </span>
    </span>
  </label>
</template>
<script setup lang="ts">
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import type { Position } from '@cutr/constants/position';
import style from './list.module.css';
import ThumbPlaceholder from './ThumbPlaceholder.vue';
import { useActivePosition } from './useActivePosition';
import RevisionReviewBadge from '@/components/elements/Order/Revisions/RevisionReviewBadge.vue';
import { getReviewStatus } from '@/components/elements/Order/Revisions/composables';
import { useActiveUserStore } from '@/stores/user';
import { useOrderStore } from '@/stores/order';

const props = defineProps<{
  position: Position;
  groupName: string;
  isActive?: boolean;
  disableDrag?: boolean;
}>();

defineEmits<{
  (e: 'selected', position: Position): void;
}>();

const { productionFiles } = useActivePosition(props.position.id);
const { activeUser } = storeToRefs(useActiveUserStore());
const { currentOrder } = storeToRefs(useOrderStore());

const productTypeTags = computed(() =>
  props.position.productTypes.map((productType) => `pt|${productType}`)
);

const productionStatus = computed(() => {
  const statuses = productionFiles.value
    .map((file) => getReviewStatus(file, activeUser.value, currentOrder.value))
    .filter(Boolean);

  if (!statuses.length) return null;

  if (statuses.some((s) => s === 'pending-review' || s === 'needs-review')) {
    return 'pending-review';
  }

  if (statuses.some((s) => s === 'rejected')) return 'rejected';

  if (statuses.every((s) => s === 'approved')) return 'approved';

  return null;
});
</script>
